import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Upload,
  Button,
  message,
} from 'antd';
import { UploadOutlined, PictureOutlined } from '@ant-design/icons';
import { uploadDishCoverImage } from '../../../services/uploadService';
import type { MenuCategory } from '../../../types/menu';
import type { CreateDishPayload } from '../../../hooks/useMerchantMenu';

const COVER_PREVIEW_SIZE = 120;

export interface AddDishModalProps {
  open: boolean;
  categories: MenuCategory[];
  onSubmit: (payload: CreateDishPayload) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const AddDishModal: React.FC<AddDishModalProps> = ({
  open,
  categories,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setCoverPreviewUrl(null);
    }
  }, [open, form]);

  const handleCoverUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      message.error('Chỉ chấp nhận file hình ảnh (jpg, png, gif, webp)');
      return;
    }
    try {
      setUploadingCover(true);
      const { url, signedUrl } = await uploadDishCoverImage(file);
      form.setFieldsValue({ coverImage: url });
      setCoverPreviewUrl(signedUrl);
      message.success('Upload ảnh bìa thành công');
    } catch (err: unknown) {
      const e = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      message.error(
        e.response?.data?.message || e.message || 'Upload ảnh thất bại'
      );
    } finally {
      setUploadingCover(false);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload: CreateDishPayload = {
        name: values.name?.trim() ?? '',
        price: Number(values.price),
        description: values.description?.trim() || null,
        categoryId:
          values.categoryId != null ? Number(values.categoryId) : null,
        coverImage: values.coverImage?.trim() || null,
        isAvailable: values.isAvailable !== false,
      };
      await onSubmit(payload);
      form.resetFields();
      onCancel();
    } catch {
      // Validation or submit error
    }
  };

  return (
    <Modal
      title="Thêm món ăn"
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      okText="Thêm món"
      cancelText="Hủy"
      destroyOnClose
      width={480}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="name"
          label="Tên món"
          rules={[{ required: true, message: 'Nhập tên món' }]}
        >
          <Input placeholder="Ví dụ: Phở bò" />
        </Form.Item>
        <Form.Item
          name="price"
          label="Giá (VNĐ)"
          rules={[
            { required: true, message: 'Nhập giá' },
            {
              validator: (_, value) => {
                const num =
                  typeof value === 'number'
                    ? value
                    : Number(String(value ?? '').replace(/,/g, ''));
                if (Number.isNaN(num) || num <= 0) {
                  return Promise.reject(new Error('Giá phải lớn hơn 0'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            min={1}
            step={1000}
            style={{ width: '100%' }}
            placeholder="45000"
            formatter={v =>
              v != null ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
            }
            parser={(v: string | undefined): number =>
              v ? Number(v.replace(/,/g, '')) : 0
            }
          />
        </Form.Item>
        <Form.Item name="description" label="Mô tả">
          <Input.TextArea rows={2} placeholder="Mô tả ngắn (tùy chọn)" />
        </Form.Item>
        <Form.Item name="categoryId" label="Danh mục">
          <Select
            placeholder={
              categories.length > 0
                ? 'Chọn danh mục (tùy chọn)'
                : 'Không tải được danh mục — thử Tải lại menu'
            }
            allowClear
            disabled={categories.length === 0}
            options={categories.map(c => ({ label: c.name, value: c.id }))}
          />
        </Form.Item>
        <Form.Item name="coverImage" label="Ảnh bìa món">
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => {
              const coverUrl = coverPreviewUrl ?? getFieldValue('coverImage');
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                  }}
                >
                  {coverUrl ? (
                    <div style={{ flexShrink: 0 }}>
                      <img
                        src={coverUrl}
                        alt="Cover"
                        style={{
                          width: COVER_PREVIEW_SIZE,
                          height: COVER_PREVIEW_SIZE,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '1px solid #d9d9d9',
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        width: COVER_PREVIEW_SIZE,
                        height: COVER_PREVIEW_SIZE,
                        border: '1px dashed #d9d9d9',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fafafa',
                      }}
                    >
                      <PictureOutlined
                        style={{ fontSize: 28, color: '#bfbfbf' }}
                      />
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <Upload
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      showUploadList={false}
                      beforeUpload={f => {
                        handleCoverUpload(f);
                        return false;
                      }}
                      disabled={uploadingCover}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={uploadingCover}
                      >
                        {coverUrl ? 'Thay ảnh' : 'Chọn ảnh (tùy chọn)'}
                      </Button>
                    </Upload>
                    {coverUrl && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        onClick={() => {
                          setFieldsValue({ coverImage: undefined });
                          setCoverPreviewUrl(null);
                        }}
                      >
                        Xóa ảnh
                      </Button>
                    )}
                  </div>
                </div>
              );
            }}
          </Form.Item>
        </Form.Item>
        <Form.Item
          name="isAvailable"
          label="Hiển thị / Bán"
          valuePropName="checked"
          initialValue={true}
        >
          <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
